import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { API_URL } from "../../../../config";

const EMPTY_FORM = { author: "", text: "" };

function normalizeTestimonialForm(form) {
  return {
    author: typeof form.author === "string" ? form.author.trim() : "",
    text: typeof form.text === "string" ? form.text.trim() : "",
  };
}

function EditarCursoTestimoniosTab({ courseId, setMensaje }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState("");
  const [editingForm, setEditingForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadTestimonials = useCallback(async () => {
    if (!courseId) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/testimonios?cursoId=${encodeURIComponent(courseId)}&limit=100`, {
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || "No se pudieron cargar los testimonios del curso.");
        return;
      }
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setError("Error de conexión al cargar testimonios.");
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    loadTestimonials();
  }, [loadTestimonials]);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateEditingForm = (field, value) => {
    setEditingForm((prev) => ({ ...prev, [field]: value }));
  };

  const createTestimonial = async (event) => {
    event.preventDefault();
    const payload = normalizeTestimonialForm(form);
    if (!payload.author || !payload.text) {
      setError("Nombre y texto del testimonio son obligatorios.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/testimonios`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...payload, cursoId: courseId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || "No se pudo añadir el testimonio.");
        return;
      }
      setForm(EMPTY_FORM);
      setMensaje("✅ Testimonio del curso añadido.");
      await loadTestimonials();
    } catch {
      setError("Error de conexión al añadir el testimonio.");
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (item) => {
    setEditingId(item._id);
    setEditingForm({ author: item.author || "", text: item.text || "" });
    setError("");
  };

  const cancelEditing = () => {
    setEditingId("");
    setEditingForm(EMPTY_FORM);
  };

  const saveEditing = async (id) => {
    const payload = normalizeTestimonialForm(editingForm);
    if (!payload.author || !payload.text) {
      setError("Nombre y texto del testimonio son obligatorios.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/testimonios/${encodeURIComponent(id)}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || "No se pudo editar el testimonio.");
        return;
      }
      setMensaje("✅ Testimonio del curso actualizado.");
      cancelEditing();
      await loadTestimonials();
    } catch {
      setError("Error de conexión al editar el testimonio.");
    } finally {
      setSaving(false);
    }
  };

  const deleteTestimonial = async (id) => {
    if (!globalThis.confirm("¿Eliminar este testimonio del curso? Esta acción no se puede deshacer.")) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/testimonios/${encodeURIComponent(id)}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || "No se pudo eliminar el testimonio.");
        return;
      }
      setMensaje("✅ Testimonio del curso eliminado.");
      await loadTestimonials();
    } catch {
      setError("Error de conexión al eliminar el testimonio.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="cc-course-testimonials">
      <h1>Testimonios del curso</h1>
      <p className="cc-course-testimonials__intro">
        Gestiona los testimonios públicos asociados a este curso. Se mostrarán en la ficha pública y en las tarjetas del catálogo.
      </p>

      <form className="cc-course-testimonials__form" onSubmit={createTestimonial}>
        <div className="form-group">
          <label htmlFor="course-testimonial-author">Nombre de la persona:</label>
          <input
            id="course-testimonial-author"
            className="form-control medio-ancho4"
            type="text"
            value={form.author}
            maxLength={100}
            onChange={(event) => updateForm("author", event.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="course-testimonial-text">Texto público del testimonio:</label>
          <textarea
            id="course-testimonial-text"
            className="form-control textarea"
            value={form.text}
            maxLength={2000}
            rows={6}
            onChange={(event) => updateForm("text", event.target.value)}
          />
        </div>
        <button type="submit" className="cc-sidebar-button guardar-button" disabled={saving || !courseId}>
          {saving ? "Guardando…" : "Añadir testimonio"}
        </button>
      </form>

      {error ? <p className="cc-message cc-course-testimonials__error">{error}</p> : null}
      {loading ? <p className="cc-message">Cargando testimonios…</p> : null}

      {items.length === 0 && !loading ? (
        <p className="cc-course-testimonials__empty">Este curso todavía no tiene testimonios asociados.</p>
      ) : (
        <ul className="cc-course-testimonials__list">
          {items.map((item) => {
            const isEditing = editingId === item._id;
            return (
              <li key={item._id} className="cc-course-testimonials__item">
                {isEditing ? (
                  <>
                    <label className="cc-course-testimonials__field">
                      <span>Nombre</span>
                      <input
                        className="form-control"
                        type="text"
                        value={editingForm.author}
                        maxLength={100}
                        onChange={(event) => updateEditingForm("author", event.target.value)}
                      />
                    </label>
                    <label className="cc-course-testimonials__field">
                      <span>Texto público</span>
                      <textarea
                        className="form-control textarea"
                        value={editingForm.text}
                        maxLength={2000}
                        rows={6}
                        onChange={(event) => updateEditingForm("text", event.target.value)}
                      />
                    </label>
                    <div className="cc-course-testimonials__actions">
                      <button type="button" className="cc-sidebar-button guardar-button" disabled={saving} onClick={() => saveEditing(item._id)}>
                        Guardar testimonio
                      </button>
                      <button type="button" className="cc-sidebar-button" disabled={saving} onClick={cancelEditing}>
                        Cancelar
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <strong>{item.author}</strong>
                    <p>{item.text}</p>
                    <div className="cc-course-testimonials__actions">
                      <button type="button" className="cc-sidebar-button" disabled={saving} onClick={() => startEditing(item)}>
                        Editar
                      </button>
                      <button type="button" className="cc-sidebar-button" disabled={saving} onClick={() => deleteTestimonial(item._id)}>
                        Eliminar
                      </button>
                    </div>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

EditarCursoTestimoniosTab.propTypes = {
  courseId: PropTypes.string.isRequired,
  setMensaje: PropTypes.func.isRequired,
};

export default EditarCursoTestimoniosTab;
